/** Weekly trend: this week's TEU and booking volume versus the prior week. */
import { openedInWeekHref } from "@/features/feed/feedLinks";
import type { WeeklyTrendInsightPayload } from "@/types/feed";

import { formatBeforeAfter, formatDeltaPct, formatNum } from "../formatInsight";
import { explainerFor } from "../insightCopy";
import { CtaLink, InsightShell, MetricGrid, PeriodBanner, type MetricItem } from "../primitives";

export function WeeklyTrendView({ payload }: { payload: WeeklyTrendInsightPayload }) {
  const data = payload.data ?? {};
  const hasTeu = typeof data.current_teu === "number" && typeof data.prior_teu === "number";
  const hasBookings =
    typeof data.current_bookings === "number" && typeof data.prior_bookings === "number";

  if (!hasTeu && !hasBookings) return null;

  const weekHref = data.week_start ? openedInWeekHref(data.week_start) : null;
  const delta =
    typeof data.teu_pct_change === "number" ? formatDeltaPct(data.teu_pct_change) : null;

  const metricItems: MetricItem[] = [];
  if (hasTeu) {
    metricItems.push({
      label: "TEU (wk / prior)",
      value: formatBeforeAfter(data.prior_teu as number, data.current_teu as number, "TEU"),
    });
  }
  if (delta) {
    metricItems.push({ label: "TEU change", value: delta.text });
  }
  if (hasBookings) {
    metricItems.push({
      label: "Bookings (wk / prior)",
      value: `${data.prior_bookings} \u2192 ${data.current_bookings}`,
    });
  }

  return (
    <InsightShell explainer={explainerFor(payload as unknown as Record<string, unknown>)}>
      {data.week_start && <PeriodBanner label={`Week of ${data.week_start}`} />}
      <MetricGrid items={metricItems} />
      {hasTeu && (
        <p className="text-[11px] text-muted-foreground">
          {formatNum(data.current_teu as number)} TEU this week
        </p>
      )}
      {weekHref && <CtaLink to={weekHref}>View this week's bookings</CtaLink>}
    </InsightShell>
  );
}
